import React, { useEffect, useState } from 'react';
import { Alert, Button, Input, Modal, Space, Spin, Tag, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { TransportRouteSummaryDto } from '../../../dto/transport';
import { listTransportRoutesForSelection } from '../../transports/api/transportRoutesApi';
import { formatDate } from '../../../utils/dateUtils';
import { buildTransportRouteLabel } from '../transportAddressUtils';
import { withTransportReturnContext } from '../../transports/transportReturnContext';
import { formatLocalizedQuantity } from '../../../utils/numberFormatting';
import { formatUnit } from '../../../utils/units';
import './OrderTransportLinkModal.css';
import { getTransportLabel } from '../../../utils/transportLabel';

const { Text } = Typography;
const TERMINAL_STATUSES = new Set(['DELIVERED', 'CANCELLED']);

interface OrderTransportLinkModalProps {
  open: boolean;
  orderId: string;
  linkedTransportIds: string[];
  onClose: () => void;
}

function buildSelectionLabel(transport: TransportRouteSummaryDto): string {
  const route = buildTransportRouteLabel(
    transport.loadingAddresses,
    transport.deliveryAddresses,
    transport.originCity,
    transport.destinationCity,
  );
  const date = formatDate(transport.loadingDate);

  return [
    getTransportLabel(transport),
    route.label,
    date,
    transport.transportCompany,
  ].filter((value) => value && value !== '-').join(' · ');
}

const OrderTransportLinkModal: React.FC<OrderTransportLinkModalProps> = ({
  open,
  orderId,
  linkedTransportIds,
  onClose,
}) => {
  const { t, i18n } = useTranslation(['transport', 'common']);
  const navigate = useNavigate();
  const [transports, setTransports] = useState<TransportRouteSummaryDto[]>([]);
  const [selectedTransportId, setSelectedTransportId] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) return undefined;

    let active = true;
    setLoading(true);
    setLoadFailed(false);
    setSelectedTransportId(undefined);
    setSearch('');

    void listTransportRoutesForSelection()
      .then((routes) => {
        if (active) setTransports(routes);
      })
      .catch(() => {
        if (active) setLoadFailed(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open]);

  const openNewTransport = () => {
    onClose();
    navigate(withTransportReturnContext(`/app/transports/new?orderId=${encodeURIComponent(orderId)}`, orderId));
  };

  const openExistingTransport = () => {
    if (!selectedTransportId) return;
    onClose();
    navigate(
      withTransportReturnContext(`/app/transports/${encodeURIComponent(selectedTransportId)}/edit?orderId=${encodeURIComponent(orderId)}`, orderId),
    );
  };

  const normalizedSearch = search.trim().toLocaleLowerCase('pl-PL');
  const visibleTransports = normalizedSearch
    ? transports.filter((transport) => {
      const searchable = [
        buildSelectionLabel(transport),
        ...transport.cargoItems.map((item) => item.productName),
        ...transport.routeOrders.map((item) => item.order.displayOrderNumber || item.order.orderNumber),
      ].join(' ').toLocaleLowerCase('pl-PL');
      return searchable.includes(normalizedSearch);
    })
    : transports;

  return (
    <Modal
      title={t('transport:orderTransports.linkDialogTitle')}
      open={open}
      onCancel={onClose}
      footer={(
        <Space wrap>
          <Button onClick={onClose}>{t('common:cancel')}</Button>
          <Button icon={<PlusOutlined />} onClick={openNewTransport}>
            {t('transport:orderTransports.newTruck')}
          </Button>
          <Button
            type="primary"
            disabled={!selectedTransportId}
            onClick={openExistingTransport}
          >
            {t('transport:orderTransports.editCargoAndLink')}
          </Button>
        </Space>
      )}
      destroyOnHidden
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Text>{t('transport:orderTransports.linkDialogDescription')}</Text>
        <Input.Search
          allowClear
          value={search}
          placeholder={t('transport:orderTransports.searchPlaceholder')}
          onChange={(event) => setSearch(event.target.value)}
        />
        {loadFailed ? (
          <Alert
            type="error"
            showIcon
            message={t('transport:orderTransports.loadExistingError')}
          />
        ) : null}
        {loading ? <Spin size="small" /> : <div className="order-transport-link-candidates">{visibleTransports.map((transport) => {
          const linked = linkedTransportIds.includes(transport.id);
          const terminal = TERMINAL_STATUSES.has(transport.status);
          const disabled = linked || terminal;
          const currentOrderCargo = transport.cargoItems.filter((item) => item.orderId === orderId);
          const cargoToDescribe = currentOrderCargo.length > 0 ? currentOrderCargo : transport.cargoItems;
          const cargoSummary = cargoToDescribe.slice(0, 2).map((item) => (
            `${item.productName}: ${formatLocalizedQuantity(item.quantity, i18n.language)} ${formatUnit(item.unit, i18n.language)}`
          )).join(' · ');
          return (
            <button
              type="button"
              key={transport.id}
              disabled={disabled}
              className={`order-transport-link-candidate${selectedTransportId === transport.id ? ' is-selected' : ''}`}
              onClick={() => setSelectedTransportId(transport.id)}
            >
              <span className="order-transport-link-candidate__description">
                <strong>{buildSelectionLabel(transport)}</strong>
                <small>{transport.routeOrders.length ? t('transport:orderTransports.linkedOrdersCount', { count: transport.routeOrders.length }) : t('transport:orderTransports.noLinkedOrders')}</small>
                <small>
                  {cargoSummary
                    ? t('transport:orderTransports.cargoSummary', { cargo: cargoSummary })
                    : t('transport:orderTransports.noCargo')}
                </small>
              </span>
              <span className="order-transport-link-candidate__badges">
                <Tag>{t(`transport:status.${transport.status.toLowerCase()}`)}</Tag>
                {linked && <Tag color="blue">{t('transport:orderTransports.alreadyLinked')}</Tag>}
                {terminal && <Tag>{t('transport:orderTransports.terminalTransport')}</Tag>}
              </span>
            </button>
          );
        })}{visibleTransports.length === 0 && <Text type="secondary">{t('transport:orderTransports.noAvailableExisting')}</Text>}</div>}
        <Alert
          type="info"
          showIcon
          message={t('transport:orderTransports.linkDialogHint')}
        />
      </Space>
    </Modal>
  );
};

export default OrderTransportLinkModal;
